import { createRoute } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@cca/ui';
import { apiRequest, ApiError } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { useRuntimeConfig } from '../lib/runtime-config.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface EvidenceItem {
  readonly label: string;
  readonly value: string;
  readonly source: string;
}

interface Proposal {
  readonly proposalId: string;
  readonly title: string;
  readonly actionType: string;
  readonly communicationClass: string;
  readonly subject: string | null;
  readonly bodyText: string | null;
  readonly language: string;
  readonly recipientCount: number;
  readonly riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  readonly requiresApproval: boolean;
  readonly autonomyLevel: string;
  readonly blocked: boolean;
  readonly blockedReason: string | null;
  readonly evidence: readonly EvidenceItem[];
  readonly assumptions: readonly string[];
  readonly missingInformation: readonly string[];
  readonly policyReasons: readonly string[];
}

interface AgentRunResponse {
  readonly runId: string;
  readonly status: string;
  readonly summary: string;
  readonly simulated: boolean;
  readonly modelProvider: string;
  readonly modelName: string;
  readonly injectionSuspected: boolean;
  readonly toolsRequested: readonly string[];
  readonly toolsDenied: readonly string[];
  readonly proposals: readonly Proposal[];
  readonly errorMessage: string | null;
}

const RISK_TONE = {
  LOW: 'neutral',
  MEDIUM: 'info',
  HIGH: 'warning',
  CRITICAL: 'danger',
} as const;

/**
 * Suggested instructions. These are real instructions the read-only tool set can
 * actually service — not aspirational examples for features that do not exist.
 */
const SUGGESTIONS = [
  'Check overdue orders and prepare customer updates.',
  'Show me orders that have had no tracking update for more than 72 hours.',
  'Which fulfilled orders have no tracking number?',
];

function ProposalCard({ proposal }: { readonly proposal: Proposal }) {
  const [showEvidence, setShowEvidence] = useState(false);

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{proposal.title}</CardTitle>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {proposal.actionType.replaceAll('_', ' ').toLowerCase()} ·{' '}
            {proposal.communicationClass.toLowerCase()} · {proposal.recipientCount} recipient
            {proposal.recipientCount === 1 ? '' : 's'} · {proposal.language}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone={RISK_TONE[proposal.riskLevel]}>{proposal.riskLevel}</Badge>
          {proposal.blocked ? (
            <Badge tone="danger">Blocked</Badge>
          ) : (
            <Badge tone="warning">Draft · approval required</Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {proposal.blocked ? (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-3 text-sm text-red-900 dark:bg-red-950 dark:text-red-200"
          >
            This proposal was blocked before it could be drafted
            {proposal.blockedReason ? ` (${proposal.blockedReason.replaceAll('_', ' ')})` : ''}. A
            person needs to interpret it.
          </div>
        ) : null}

        {proposal.subject ? (
          <p className="text-sm">
            <span className="text-slate-500 dark:text-slate-400">Subject: </span>
            <span className="font-medium text-slate-900 dark:text-slate-100">
              {proposal.subject}
            </span>
          </p>
        ) : null}

        {proposal.bodyText ? (
          <div className="rounded-md border border-slate-200 bg-white p-3 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200">
            {proposal.bodyText}
          </div>
        ) : null}

        {proposal.policyReasons.length > 0 ? (
          <ul className="space-y-1">
            {proposal.policyReasons.map((reason, index) => (
              <li key={index} className="text-xs text-amber-800 dark:text-amber-300">
                • {reason}
              </li>
            ))}
          </ul>
        ) : null}

        {proposal.missingInformation.length > 0 ? (
          <div className="rounded-md bg-slate-50 p-3 dark:bg-slate-950">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Not known yet — deliberately not filled in
            </p>
            <ul className="mt-1 space-y-0.5">
              {proposal.missingInformation.map((item, index) => (
                <li key={index} className="text-xs text-slate-600 dark:text-slate-400">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {proposal.evidence.length > 0 ? (
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="px-0"
              aria-expanded={showEvidence}
              onClick={() => setShowEvidence(!showEvidence)}
            >
              {showEvidence ? 'Hide facts used' : `Show facts used (${proposal.evidence.length})`}
            </Button>

            {showEvidence ? (
              <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 rounded-md bg-slate-50 p-3 sm:grid-cols-2 dark:bg-slate-950">
                {proposal.evidence.map((item, index) => (
                  <div key={index} className="flex justify-between gap-3 text-xs">
                    <dt className="text-slate-500 dark:text-slate-400">{item.label}</dt>
                    <dd className="text-right font-medium text-slate-800 dark:text-slate-200">
                      {item.value}
                      <span className="ml-1 font-normal text-slate-400">({item.source})</span>
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AgentPage() {
  const config = useRuntimeConfig();
  const accessToken = useAccessToken();
  const [instruction, setInstruction] = useState(SUGGESTIONS[0]!);

  const run = useMutation({
    mutationFn: (text: string) =>
      apiRequest<AgentRunResponse>('/api/agent/runs', {
        method: 'POST',
        body: { instruction: text },
        accessToken,
      }),
  });

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {config.product.agentName}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Ask in plain language. The agent reads verified order data and prepares drafts for you to
          review. It cannot send anything.
        </p>
      </header>

      <Card>
        <CardContent className="space-y-3">
          <label htmlFor="instruction" className="block text-sm font-medium">
            Instruction
          </label>
          <textarea
            id="instruction"
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            rows={3}
            className="w-full rounded-md border border-slate-300 bg-white p-3 text-sm focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none dark:border-slate-700 dark:bg-slate-950"
          />

          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((suggestion) => (
              <Button
                key={suggestion}
                variant="outline"
                size="sm"
                onClick={() => setInstruction(suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => run.mutate(instruction)}
              disabled={run.isPending || instruction.trim().length < 3}
            >
              {run.isPending ? 'Running…' : 'Run agent'}
            </Button>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Produces drafts only. Nothing is sent.
            </span>
          </div>
        </CardContent>
      </Card>

      {run.isPending ? <LoadingState label="The agent is working" /> : null}

      {run.isError ? (
        <ErrorState
          title="The run could not start"
          description={run.error instanceof ApiError ? run.error.message : 'Unexpected error.'}
          onRetry={() => run.mutate(instruction)}
        />
      ) : null}

      {run.data ? (
        <section aria-label="Run result" className="space-y-3">
          <Card>
            <CardHeader>
              <CardTitle>Result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-slate-800 dark:text-slate-200">{run.data.summary}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Run {run.data.runId} · {run.data.status.toLowerCase()} · model{' '}
                {run.data.modelProvider}/{run.data.modelName}
                {run.data.simulated ? ' (simulated)' : ''}
              </p>

              {run.data.toolsRequested.length > 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Tools used: {run.data.toolsRequested.join(', ')}
                </p>
              ) : null}

              {run.data.toolsDenied.length > 0 ? (
                <p className="text-xs text-red-700 dark:text-red-400">
                  Tools denied: {run.data.toolsDenied.join(', ')} — recorded in the audit log.
                </p>
              ) : null}

              {run.data.injectionSuspected ? (
                <div
                  role="alert"
                  className="rounded-md bg-red-50 p-3 text-xs text-red-900 dark:bg-red-950 dark:text-red-200"
                >
                  The source content contained text resembling instructions aimed at the assistant.
                  It was treated as customer data and the risk level was raised.
                </div>
              ) : null}

              {run.data.errorMessage ? (
                <p className="text-xs text-red-700 dark:text-red-400">{run.data.errorMessage}</p>
              ) : null}
            </CardContent>
          </Card>

          {run.data.proposals.length === 0 ? (
            <Card>
              <EmptyState
                title="No proposals"
                description="The agent did not find anything that needs a customer update right now."
              />
            </Card>
          ) : (
            <ul className="space-y-3">
              {run.data.proposals.map((proposal) => (
                <li key={proposal.proposalId}>
                  <ProposalCard proposal={proposal} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}

export const agentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agent',
  component: () => (
    <RequireSession>
      <AgentPage />
    </RequireSession>
  ),
});
